import React, { useState, useEffect } from 'react';
import { adminApi, discountCodesApi } from '../services/api';
import api from '../services/api';
import { format, subDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { TripDate } from '../types';
import TripCostCalculator from '../components/TripCostCalculator';



interface DashboardData {
  totalUsers: number;
  totalTrips: number;
  totalPayments: number;
  totalRefundRequests: number;
  recentUsers: number;
  recentPayments: number;
  pendingRefunds: number;
  totalPaymentAmount: number;
  totalRefundAmount: number;
  failedPayments: number;
  pendingPayments: number;
  refundedPayments: number;
  totalProducts: number;
  totalProductOrders: number;
  totalProductRevenue: number;
  lowStockProducts: number;
  paymentsWithDiscount: number;
  totalDiscountAmount: number;
}

interface User {
  id: string;
  email: string;
  username: string;
  isAdmin: boolean;
  createdAt: string;
  active: boolean;
  bookings: Booking[];
  payments: Payment[];
  refundRequests: RefundRequest[];
  _count: {
    bookings: number;
    payments: number;
    refundRequests: number;
  };
}

interface Trip {
  id: string;
  name: string;
  description: string;
  deposit: number;
  startDate?: string;
  endDate?: string;
  maxParticipants?: number;
  isActive: boolean;
  createdAt: string;
  tripDates?: TripDate[];
  priceSingle?: number;
  priceDuo?: number;
  priceTrio?: number;
  pricePocker?: number;
  confirmedBookingsCount?: number;
  totalParticipants?: number;
  _count: {
    bookings: number;
    payments: number;
  };
}

interface Booking {
  id: string;
  userId: string;
  tripId: string;
  numberOfQuotes: number;
  totalAmount: number;
  status: string;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    username: string;
  };
  trip?: {
    id: string;
    name: string;
  };
  participants: Participant[];
  payments: Payment[];
}

interface Participant {
  id: string;
  firstName: string;
  lastName: string;
  order: number;
}

interface Payment {
  id: string;
  userId: string;
  tripId: string;
  bookingId: string;
  amount: number;
  tranche: number;
  status: string;
  paidAt: string;
  failedAt: string;
  refundedAt: string;
  createdAt: string;
  participantCount: number;
  participants: Participant[];
  // Discount code fields
  importoOriginale?: number | null;
  importoFinale?: number | null;
  percentualeScontoApplicata?: number | null;
  codiceScontoId?: string | null;
  codiceSconto?: {
    id: string;
    codice: string;
    percentualeSconto: number;
  } | null;
  user?: {
    id: string;
    email: string;
    username: string;
  };
  trip?: {
    id: string;
    name: string;
    startDate?: string;
    endDate?: string;
  };
  booking?: Booking;
  refundRequests: RefundRequest[];
}

interface RefundRequest {
  id: string;
  paymentId: string;
  userId: string;
  reason: string;
  status: string;
  adminNotes: string;
  processedAt: string;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    username: string;
  };
  payment?: {
    id: string;
    amount: number;
    trip?: {
      id: string;
      name: string;
      startDate?: string;
      endDate?: string;
    };
  };
}

interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  slug: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProductOrder {
  id: string;
  userId: string;
  totalAmount: number;
  status: string;
  createdAt: string;
  paidAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  user?: {
    id: string;
    email: string;
    username: string;
  };
  items: ProductOrderItem[];
  shippingAddress?: string;
  shippingCity?: string;
  shippingPostalCode?: string;
  shippingCountry?: string;
}

interface ProductOrderItem {
  id: string;
  quantity: number;
  priceAtTime: number;
  product: {
    id: string;
    name: string;
  };
}

interface ShippingCost {
  id: string;
  nameSlug: string;
  cost: number;
  minOrderAmount: number | null;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  countries: string[];
  createdAt: string;
  updatedAt: string;
}

const AdminDashboardPage: React.FC = () => {
  // Section states
  const [activeGeneraleTab, setActiveGeneraleTab] = useState('overview');
  const [activeViaggiTab, setActiveViaggiTab] = useState('trips');
  const [activeProdottiTab, setActiveProdottiTab] = useState('products');
  
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [adminBookings, setAdminBookings] = useState<any[]>([]);
  const [adminBookingsPagination, setAdminBookingsPagination] = useState<{ page: number; limit: number; total: number; pages: number }>({ page: 1, limit: 20, total: 0, pages: 0 });
  const [trips, setTrips] = useState<Trip[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [refunds, setRefunds] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRefund, setSelectedRefund] = useState<RefundRequest | null>(null);
  const [refundStatus, setRefundStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [tripPricing, setTripPricing] = useState({
    deposit: 0,
    priceSingle: 0,
    priceDuo: 0,
    priceTrio: 0,
    pricePocker: 0,
    maxParticipants: 20,
    isActive: true
  });
  const [showCreateTripModal, setShowCreateTripModal] = useState(false);
  const [newTrip, setNewTrip] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    maxParticipants: 20,
    isActive: true,
    deposit: 0,
    priceSingle: 0,
    priceDuo: 0,
    priceTrio: 0,
    pricePocker: 0
  });
  const [tripPricingConfig, setTripPricingConfig] = useState({
    solo: { totalPrice: 0, depositPerPerson: 0, secondTrancheAmount: 0 },
    duo: { totalPrice: 0, depositPerPerson: 0, secondTrancheAmount: 0 },
    family3: { totalPrice: 0, depositPerPerson: 0, secondTrancheAmount: 0 },
    family4: { totalPrice: 0, depositPerPerson: 0, secondTrancheAmount: 0 }
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedPaymentParticipants, setSelectedPaymentParticipants] = useState<Payment | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [productOrders, setProductOrders] = useState<ProductOrder[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedProductOrder, setSelectedProductOrder] = useState<ProductOrder | null>(null);
  const [shippingCosts, setShippingCosts] = useState<ShippingCost[]>([]);
  const [selectedShippingCost, setSelectedShippingCost] = useState<ShippingCost | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [showSalesReport, setShowSalesReport] = useState(false);
  const [salesReportDateRange, setSalesReportDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [salesReportLoading, setSalesReportLoading] = useState(false);
  
  // Trip configuration states (removed - using hardcoded values)
  const [tripDates, setTripDates] = useState<any[]>([]);
  const [selectedTripDate, setSelectedTripDate] = useState<any>(null);
  const [tripPricingList, setTripPricingList] = useState<any[]>([]);
  const [selectedTripPricing, setSelectedTripPricing] = useState<any>(null);
  const [newTripDate, setNewTripDate] = useState({
    startDate: '',
    endDate: ''
  });
  const [newTripPricing, setNewTripPricing] = useState({
    participationType: '',
    occupancy: 1,
    totalPrice: 0,
    depositPerPerson: 0,
    secondTrancheAmount: 0
  });
  const [salesReport, setSalesReport] = useState<any>(null);
  const [salesReportFilters, setSalesReportFilters] = useState({
    productId: '',
    category: '',
    customerEmail: ''
  });
  const [discountCodes, setDiscountCodes] = useState<any[]>([]);
  const [discountCodeStats, setDiscountCodeStats] = useState<any>(null);
  const [newDiscountCode, setNewDiscountCode] = useState({
    percentualeSconto: 0,
    viaggioId: '',
    nomeInfluencer: '',
    cognomeInfluencer: ''
  });
  const [selectedDiscountCode, setSelectedDiscountCode] = useState<any | null>(null);
  const [editDiscountCode, setEditDiscountCode] = useState({
    percentualeSconto: 0,
    viaggioId: '',
    nomeInfluencer: '',
    cognomeInfluencer: ''
  });
  const [showEditDiscountCodeModal, setShowEditDiscountCodeModal] = useState(false);

  useEffect(() => {
    // Load data based on active tabs in each section
    if (activeGeneraleTab === 'overview') {
      loadDashboardData();
    } else if (activeGeneraleTab === 'users') {
      loadUsers();
    } else if (activeGeneraleTab === 'payments') {
      loadPayments();
    } else if (activeGeneraleTab === 'refunds') {
      loadRefunds();
    } else if (activeGeneraleTab === 'bookings') {
      loadAdminBookings();
    } else if (activeGeneraleTab === 'discount-codes') {
      loadDiscountCodes();
      loadDiscountCodeStats();
      if (trips.length === 0) {
        loadTrips();
      }
    }
    
    if (activeViaggiTab === 'trips') {
      loadTrips();
    }
    
    if (activeProdottiTab === 'products') {
      loadProducts();
      loadCategories();
    } else if (activeProdottiTab === 'categories') {
      loadCategories();
    } else if (activeProdottiTab === 'product-orders') {
      loadProductOrders();
    } else if (activeProdottiTab === 'shipping-costs') {
      loadShippingCosts();
    }
  }, [activeGeneraleTab, activeViaggiTab, activeProdottiTab, currentPage, searchTerm, dateRange]);

  useEffect(() => {
    loadDashboardData();
    if (activeGeneraleTab === 'discount-codes') {
      loadDiscountCodeStats();
    }
  }, [dateRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getDashboard({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      setDashboardData(response.dashboard);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getUsersDetailed({
        page: currentPage,
        limit: 10,
        search: searchTerm || undefined
      });
      setUsers(response.users);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrips = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTripsDetailed({
        page: currentPage,
        limit: 10
      });
      setTrips(response.trips);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Error loading trips:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getPaymentsDetailed({
        page: currentPage,
        limit: 10
      });
      setPayments(response.payments);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRefunds = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getRefundsDetailed({
        page: currentPage,
        limit: 10
      });
      setRefunds(response.refunds);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Error loading refunds:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminBookings = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getBookingsDetailed({ page: currentPage, limit: itemsPerPage, userSearch: searchTerm });
      setAdminBookings(response.bookings || []);
      if (response.pagination) setAdminBookingsPagination(response.pagination);
    } catch (error) {
      console.error('Error loading admin bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading products from API...');
      const response = await adminApi.getProducts();
      console.log('📥 API Response:', response);
      console.log('📦 Products data:', response.data || response);
      setProducts(response.data || response || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProductOrders = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getProductOrders();
      setProductOrders(response.data || []);
    } catch (error) {
      console.error('Error loading product orders:', error);
      setProductOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadShippingCosts = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getShippingCosts();
      setShippingCosts(response || []);
    } catch (error) {
      console.error('Error loading shipping costs:', error);
      setShippingCosts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDiscountCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/discount-codes');
      setDiscountCodes(response.data?.codiciSconto || []);
    } catch (error) {
      console.error('Error loading discount codes:', error);
      setDiscountCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDiscountCodeStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/discount-codes/stats', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
      setDiscountCodeStats(response.data?.stats || null);
    } catch (error) {
      console.error('Error loading discount code stats:', error);
      setDiscountCodeStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRefundStatusUpdate = async () => {
    if (!selectedRefund || !refundStatus) return;

    try {
      await adminApi.updateRefundStatus(selectedRefund.id, {
        status: refundStatus,
        adminNotes: adminNotes
      });
      
      loadRefunds();
      setSelectedRefund(null);
      setRefundStatus('');
      setAdminNotes('');
    } catch (error) {
      console.error('Error updating refund status:', error);
    }
  };

  const openRefundModal = (refund: RefundRequest) => {
    setSelectedRefund(refund);
    setRefundStatus(refund.status); // Pre-populate with current status
    setAdminNotes(refund.adminNotes || ''); // Pre-populate with existing notes
  };

  const handleTripPricingUpdate = async () => {
    if (!selectedTrip) return;

    try {
      await adminApi.updateTrip(selectedTrip.id, tripPricing);
      
      loadTrips();
      setSelectedTrip(null);
      setTripPricing({
        deposit: 0,
        priceSingle: 0,
        priceDuo: 0,
        priceTrio: 0,
        pricePocker: 0,
        maxParticipants: 20,
        isActive: true
      });
    } catch (error) {
      console.error('Error updating trip:', error);
    }
  };

  const handleTripDelete = async (tripId: string, tripName: string) => {
    if (!confirm(`Sei sicuro di voler eliminare il viaggio "${tripName}"? Questa azione non può essere annullata.`)) {
      return;
    }

    try {
      await adminApi.deleteTrip(tripId);
      loadTrips();
    } catch (error: any) {
      console.error('Error deleting trip:', error);
      const errorMessage = error.response?.data?.message || 'Errore nell\'eliminazione del viaggio. Assicurati che non ci siano prenotazioni o pagamenti associati.';
      alert(errorMessage);
    }
  };

  const handleUserPromotion = async (userId: string, isAdmin: boolean) => {
    try {
      await adminApi.promoteUser(userId, isAdmin);
      
      loadUsers();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user admin status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
      case 'confirmed':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'failed':
      case 'cancelled':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'refunded':
      case 'processed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: { [key: string]: string } = {
      'pending': 'In attesa',
      'paid': 'Pagato',
      'shipped': 'Spedito',
      'delivered': 'Consegnato',
      'cancelled': 'Annullato',
      'confirmed': 'Confermato',
      'failed': 'Fallito',
      'refunded': 'Rimborsato',
      'processed': 'Processato',
      'rejected': 'Rifiutato'
    };
    return statusLabels[status] || status;
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'paid':
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'refunded':
      case 'processed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: it });
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return '€0,00';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatTripOption = (trip: Trip) => {
    let text = trip.name;
    if (trip.startDate) {
      const startDate = new Date(trip.startDate).toLocaleDateString('it-IT');
      if (trip.endDate && trip.endDate !== trip.startDate) {
        const endDate = new Date(trip.endDate).toLocaleDateString('it-IT');
        text += ` (${startDate} - ${endDate})`;
      } else {
        text += ` (${startDate})`;
      }
    }
    return text;
  };

  const generaleTabs = [
    { id: 'overview', name: 'Panoramica' },
    { id: 'users', name: 'Utenti' },
    { id: 'payments', name: 'Pagamenti' },
    { id: 'refunds', name: 'Rimborsi' },
    { id: 'bookings', name: 'Prenotazioni viaggi' },
    { id: 'discount-codes', name: 'Codici Sconto' }
  ];

  const viaggiTabs = [
    { id: 'trips', name: 'Viaggi' },
    { id: 'destinations', name: 'Destinazioni' },
    { id: 'activities', name: 'Attività' }
  ];

  const prodottiTabs = [
    { id: 'products', name: 'Prodotti' },
    { id: 'categories', name: 'Categorie' },
    { id: 'product-orders', name: 'Ordini Prodotti' },
    { id: 'shipping-costs', name: 'Costi di Consegna' }
  ];

  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProduct) return;

    try {
      if (selectedProduct.id) {
        await adminApi.updateProduct(selectedProduct.id, selectedProduct);
      } else {
        await adminApi.createProduct(selectedProduct);
      }
      loadProducts();
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) return;

    try {
      if (selectedCategory.id) {
        await api.put(`/categories/${selectedCategory.id}`, selectedCategory);
      } else {
        await api.post('/categories', selectedCategory);
      }
      loadCategories();
      setSelectedCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa categoria?')) {
      return;
    }

    try {
      await api.delete(`/categories/${categoryId}`);
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Errore nell\'eliminazione della categoria. Assicurati che non ci siano prodotti associati.');
    }
  };

  const handleOrderStatusUpdate = async (orderId: string, newStatus: string) => {
    try {
      await adminApi.updateProductOrderStatus(orderId, newStatus);
      loadProductOrders();
      setSelectedProductOrder(null);
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const generateSalesReport = async () => {
    setSalesReportLoading(true);
    try {
      const response = await adminApi.getSalesReport({
        startDate: salesReportDateRange.startDate,
        endDate: salesReportDateRange.endDate,
        ...salesReportFilters
      });
      setSalesReport(response.report);
    } catch (error) {
      console.error('Error generating sales report:', error);
      setSalesReport(null);
    } finally {
      setSalesReportLoading(false);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    try {
      await adminApi.deleteProduct(productId);
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleShippingCostSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShippingCost) return;

    try {
      if (selectedShippingCost.id) {
        await adminApi.updateShippingCost(selectedShippingCost.id, selectedShippingCost);
      } else {
        await adminApi.createShippingCost(selectedShippingCost);
      }
      loadShippingCosts();
      setSelectedShippingCost(null);
    } catch (error) {
      console.error('Error saving shipping cost:', error);
    }
  };

  const handleDeleteShippingCost = async (shippingCostId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo costo di consegna?')) {
      return;
    }

    try {
      await adminApi.deleteShippingCost(shippingCostId);
      loadShippingCosts();
    } catch (error) {
      console.error('Error deleting shipping cost:', error);
      alert('Errore nell\'eliminazione del costo di consegna.');
    }
  };

  // Individual content render functions
  const renderBookingsContent = () => (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="Cerca per email o username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {loading ? (
          <div className="text-center py-4">Caricamento...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Viaggio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partecipanti</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pagamento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creato</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(adminBookings) && adminBookings.map((b) => (
                  <tr key={b.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{b.trip?.name}</div>
                      <div className="text-xs text-gray-500">{b.participationType?.toUpperCase()} • {b.numberOfQuotes} persone</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {b.trip?.startDate ? format(new Date(b.trip.startDate), 'dd/MM/yyyy', { locale: it }) : '-'}
                      {' - '}
                      {b.trip?.endDate ? format(new Date(b.trip.endDate), 'dd/MM/yyyy', { locale: it }) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{b.user?.username || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{b.user?.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {b.participants && b.participants.length > 0 ? (
                          <div className="space-y-1">
                            {b.participants.map((p: any) => (
                              <div key={p.id} className="text-xs text-gray-700">
                                {p.order}. {p.firstName} {p.lastName}{typeof p.age === 'number' ? ` (${p.age})` : ''}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex w-fit px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(b.paymentSummary?.deposit || 'none')}`}>
                          Acconto: {getStatusLabel(b.paymentSummary?.deposit || 'none')}
                        </span>
                        <span className={`inline-flex w-fit px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(b.paymentSummary?.balance || 'none')}`}>
                          Saldo: {getStatusLabel(b.paymentSummary?.balance || 'none')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(b.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {adminBookingsPagination && adminBookingsPagination.pages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Pagina {adminBookingsPagination.page} di {adminBookingsPagination.pages} — Totale {adminBookingsPagination.total}
            </div>
            <div className="space-x-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className={`px-3 py-1 rounded-md text-sm ${currentPage <= 1 ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Precedente
              </button>
              <button
                disabled={currentPage >= adminBookingsPagination.pages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className={`px-3 py-1 rounded-md text-sm ${currentPage >= adminBookingsPagination.pages ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Successiva
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  const renderUsersContent = () => (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="mb-4">
          <input
            type="text"
            placeholder="Cerca per email o username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        {loading ? (
          <div className="text-center py-4">Caricamento...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ruolo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prenotazioni
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pagamenti
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rimborsi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Registrazione
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(users) && users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.username || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.isAdmin ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.isAdmin ? 'Admin' : 'Utente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user._count.bookings}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user._count.payments}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user._count.refundRequests}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleUserPromotion(user.id, !user.isAdmin)}
                        className={`text-sm px-3 py-1 rounded-md ${
                          user.isAdmin 
                            ? 'text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100' 
                            : 'text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100'
                        }`}
                      >
                        {user.isAdmin ? 'Rimuovi Admin' : 'Promuovi Admin'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderPaymentsContent = () => (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        {loading ? (
          <div className="text-center py-4">Caricamento...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Viaggio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Importo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prezzo Scontato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Codice Sconto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tranche
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partecipanti
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(payments) && payments.map((payment) => {
                  // Calculate original and discounted amounts
                  let originalAmount = payment.amount;
                  let discountedAmount = payment.amount;
                  
                  if (payment.tranche === 1) {
                    // Deposit: original = discounted (deposit is not discounted)
                    originalAmount = payment.amount;
                    discountedAmount = payment.amount;
                  } else if (payment.tranche === 2) {
                    // Balance: payment.amount is already the discounted balance
                    // Calculate original balance: total original - deposit
                    // Try to get deposit amount from booking payments or calculate from importoOriginale
                    const depositPayment = payment.booking?.payments?.find((p: Payment) => p.tranche === 1);
                    const depositAmount = depositPayment?.amount || 0;
                    
                    if (payment.importoOriginale && depositAmount > 0) {
                      // Original balance = total original - deposit
                      originalAmount = payment.importoOriginale - depositAmount;
                    } else {
                      // If we don't have importoOriginale, use payment.amount as original (no discount applied)
                      originalAmount = payment.amount;
                    }
                    
                    // Discounted balance is payment.amount (already discounted if there's a discount)
                    discountedAmount = payment.amount;
                  }
                  
                  return (
                  <tr key={payment.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {payment.user?.username || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">{payment.user?.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{payment.trip?.name}</div>
                        {payment.trip?.startDate && (
                          <div className="text-xs text-gray-500">
                            {new Date(payment.trip.startDate).toLocaleDateString('it-IT')}
                            {payment.trip.endDate && payment.trip.endDate !== payment.trip.startDate && 
                              ` - ${new Date(payment.trip.endDate).toLocaleDateString('it-IT')}`
                            }
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(originalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(discountedAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.codiceSconto ? (
                        <div>
                          <div className="font-medium text-green-600">{payment.codiceSconto.codice}</div>
                          {payment.percentualeScontoApplicata && (
                            <div className="text-xs text-gray-500">-{payment.percentualeScontoApplicata}%</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.tranche === 1 ? 'Acconto' : 'Saldo'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div 
                        className="text-sm text-gray-900 cursor-pointer hover:text-blue-600"
                        onClick={() => setSelectedPaymentParticipants(payment)}
                      >
                        <div className="font-medium">{payment.participantCount} persone</div>
                        {payment.participantCount > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {payment.participants.slice(0, 2).map(p => `${p.firstName} ${p.lastName}`).join(', ')}
                            {payment.participantCount > 2 && ` +${payment.participantCount - 2} altri`}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                        {payment.status === 'paid' ? 'Pagato' :
                         payment.status === 'pending' ? 'In Attesa' :
                         payment.status === 'failed' ? 'Fallito' :
                         payment.status === 'refunded' ? 'Rimborsato' : payment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(payment.createdAt)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderRefundsContent = () => (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        {loading ? (
          <div className="text-center py-4">Caricamento...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Viaggio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Importo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Motivo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Richiesta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(refunds) && refunds.map((refund) => (
                  <tr key={refund.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {refund.user?.username || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">{refund.user?.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{refund.payment?.trip?.name}</div>
                        {refund.payment?.trip?.startDate && (
                          <div className="text-xs text-gray-500">
                            {new Date(refund.payment.trip.startDate).toLocaleDateString('it-IT')}
                            {refund.payment.trip.endDate && refund.payment.trip.endDate !== refund.payment.trip.startDate && 
                              ` - ${new Date(refund.payment.trip.endDate).toLocaleDateString('it-IT')}`
                            }
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(refund.payment?.amount || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="max-w-xs truncate" title={refund.reason}>
                        {refund.reason}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(refund.status)}`}>
                        {refund.status === 'pending' ? 'In Attesa' :
                         refund.status === 'approved' ? 'Approvato' :
                         refund.status === 'rejected' ? 'Rifiutato' :
                         refund.status === 'processed' ? 'Processato' : refund.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(refund.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openRefundModal(refund)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Gestisci
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderTripsContent = () => (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Gestione Viaggi</h3>
          <button
            onClick={() => setShowCreateTripModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Crea Nuovo Viaggio
          </button>
        </div>
        {loading ? (
          <div className="text-center py-4">Caricamento...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Viaggio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prezzi per Partecipazione
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acconto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prenotazioni
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pagamenti
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(trips) && trips.map((trip) => (
                  <tr key={trip.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{trip.name}</div>
                        {trip.startDate && (
                          <div className="text-xs text-blue-600 mt-1">
                            {new Date(trip.startDate).toLocaleDateString('it-IT')}
                            {trip.endDate && trip.endDate !== trip.startDate && 
                              ` - ${new Date(trip.endDate).toLocaleDateString('it-IT')}`
                            }
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-xs">
                          <span className="font-medium">Solo:</span>
                          <span className="text-gray-600 ml-1">
                            {formatCurrency(trip.priceSingle)}
                          </span>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium">Duo:</span>
                          <span className="text-gray-600 ml-1">
                            {formatCurrency(trip.priceDuo)}
                          </span>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium">Trio:</span>
                          <span className="text-gray-600 ml-1">
                            {formatCurrency(trip.priceTrio)}
                          </span>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium">Pocker:</span>
                          <span className="text-gray-600 ml-1">
                            {formatCurrency(trip.pricePocker)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(trip.deposit)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div>Prenotazioni: {trip.confirmedBookingsCount || 0}</div>
                        {trip.maxParticipants && (
                          <div className="text-xs text-gray-500">
                            <div>Max: {trip.maxParticipants}</div>
                            <div>Disponibili: {trip.maxParticipants - (trip.totalParticipants || 0)}</div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trip._count.payments}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        trip.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {trip.isActive ? 'Attivo' : 'Inattivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedTrip(trip);
                            setTripPricing({
                              deposit: trip.deposit,
                              priceSingle: trip.priceSingle || 0,
                              priceDuo: trip.priceDuo || 0,
                              priceTrio: trip.priceTrio || 0,
                              pricePocker: trip.pricePocker || 0,
                              maxParticipants: trip.maxParticipants || 20,
                              isActive: trip.isActive
                            });
                          }}
                          className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md"
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => handleTripDelete(trip.id, trip.name)}
                          className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md"
                        >
                          Elimina
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {Array.isArray(trips) && trips.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-500 text-lg">Nessun viaggio trovato</div>
            <div className="text-gray-400 text-sm mt-2">Aggiungi un nuovo viaggio per iniziare</div>
          </div>
        )}
      </div>
    </div>
  );

  const renderProductsContent = () => (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestione Prodotti</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSalesReportDateRange({
                startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
                endDate: format(new Date(), 'yyyy-MM-dd')
              });
              generateSalesReport();
              setShowSalesReport(true);
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Report Vendite
          </button>
          <button
            onClick={() => setSelectedProduct({} as Product)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Aggiungi Prodotto
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Caricamento prodotti...</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {Array.isArray(products) && products.map((product) => (
              <li key={product.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12">
                      <div className="h-12 w-12 rounded-md bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">No img</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{product.name}</p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {product.isActive ? 'Attivo' : 'Inattivo'}
                        </span>
                      </div>

                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-sm text-gray-600">€{product.price.toFixed(2)}</span>
                        <span className={`text-sm px-2 py-1 rounded ${
                          product.stock > 10 ? 'bg-green-100 text-green-800' :
                          product.stock > 0 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          Stock: {product.stock}
                        </span>
                        {product.category && (
                          <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {product.category.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedProduct(product)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Modifica
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Sei sicuro di voler eliminare questo prodotto?')) {
                          handleDeleteProduct(product.id);
                        }
                      }}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const renderCategoriesContent = () => (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestione Categorie</h2>
        <button
          onClick={() => setSelectedCategory({ id: '', name: '', slug: '', isActive: true, createdAt: '', updatedAt: '' })}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Aggiungi Categoria
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Caricamento categorie...</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {Array.isArray(categories) && categories.map((category) => (
              <li key={category.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{category.name}</p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          category.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {category.isActive ? 'Attiva' : 'Inattiva'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">Slug: {category.slug}</p>
                      <p className="text-sm text-gray-500">Creata: {formatDate(category.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedCategory(category)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const renderProductOrdersContent = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Ordini Prodotti</h2>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Caricamento ordini...</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {Array.isArray(productOrders) && productOrders.map((order) => (
              <li key={order.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Ordine #{order.id.slice(-8)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {order.user?.email || 'Utente non trovato'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(order.createdAt)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(order.totalAmount)}
                        </p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        Prodotti: {order.items.map(item => `${item.product.name} (${item.quantity})`).join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setSelectedProductOrder(order)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Dettagli
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const renderShippingCostsContent = () => (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestione Costi di Consegna</h2>
        <button
          onClick={() => setSelectedShippingCost({ 
            id: '', 
            nameSlug: '', 
            cost: 0, 
            minOrderAmount: null, 
            isActive: true, 
            isDefault: false, 
            sortOrder: 0, 
            countries: ['NL'],
            createdAt: '', 
            updatedAt: '' 
          })}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Aggiungi Costo di Consegna
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Caricamento costi di consegna...</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {Array.isArray(shippingCosts) && shippingCosts.map((shippingCost) => (
              <li key={shippingCost.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{shippingCost.nameSlug}</p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          shippingCost.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {shippingCost.isActive ? 'Attivo' : 'Inattivo'}
                        </span>
                        {shippingCost.isDefault && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Predefinito
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        Costo: €{shippingCost.cost.toFixed(2)}
                        {shippingCost.minOrderAmount && ` | Min. ordine: €${shippingCost.minOrderAmount.toFixed(2)}`}
                      </p>
                      <p className="text-sm text-gray-500">
                        Paesi: {shippingCost.countries.join(', ')}
                      </p>
                      <p className="text-sm text-gray-500">Ordine: {shippingCost.sortOrder}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedShippingCost(shippingCost)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDeleteShippingCost(shippingCost.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // Render functions for each section
  const renderGeneraleContent = () => {
    if (activeGeneraleTab === 'overview') {
      return (
        <div>
          {/* Date Range Filter */}
          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filtro Periodo</h3>
            <div className="flex flex-wrap gap-4 items-center">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Inizio</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Fine</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setDateRange({
                    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
                    endDate: format(new Date(), 'yyyy-MM-dd')
                  })}
                  className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                >
                  Ultimi 7 giorni
                </button>
                <button
                  onClick={() => setDateRange({
                    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
                    endDate: format(new Date(), 'yyyy-MM-dd')
                  })}
                  className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                >
                  Ultimi 30 giorni
                </button>
                <button
                  onClick={() => setDateRange({
                    startDate: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
                    endDate: format(new Date(), 'yyyy-MM-dd')
                  })}
                  className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                >
                  Ultimi 90 giorni
                </button>
              </div>
            </div>
          </div>

          {/* Dashboard Cards */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Caricamento statistiche...</p>
            </div>
          ) : dashboardData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Total Users Card */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Utenti Totali</dt>
                        <dd className="text-lg font-medium text-gray-900">{dashboardData.totalUsers}</dd>
                        <dt className="text-sm font-medium text-gray-500 truncate mt-1">Nel periodo selezionato</dt>
                        <dd className="text-sm text-blue-600">{dashboardData.recentUsers}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Payments Card */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Pagamenti Ricevuti</dt>
                        <dd className="text-lg font-medium text-gray-900">{dashboardData.recentPayments}</dd>
                        <dt className="text-sm font-medium text-gray-500 truncate mt-1">Importo Totale</dt>
                        <dd className="text-sm text-green-600">{formatCurrency(dashboardData.totalPaymentAmount)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Discount Codes Statistics Card */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Pagamenti con Codice Sconto</dt>
                        <dd className="text-lg font-medium text-gray-900">{dashboardData.paymentsWithDiscount}</dd>
                        <dt className="text-sm font-medium text-gray-500 truncate mt-1">Totale Sconti Applicati</dt>
                        <dd className="text-sm text-purple-600">{formatCurrency(dashboardData.totalDiscountAmount)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Failed Payments Card */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Pagamenti Falliti</dt>
                        <dd className="text-lg font-medium text-gray-900">{dashboardData.failedPayments}</dd>
                        <dt className="text-sm font-medium text-gray-500 truncate mt-1">Nel periodo selezionato</dt>
                        <dd className="text-sm text-red-600">Tasso di fallimento</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Payments Card */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Pagamenti in Attesa</dt>
                        <dd className="text-lg font-medium text-gray-900">{dashboardData.pendingPayments}</dd>
                        <dt className="text-sm font-medium text-gray-500 truncate mt-1">Nel periodo selezionato</dt>
                        <dd className="text-sm text-yellow-600">In elaborazione</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Refunded Payments Card */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Pagamenti Rimborsati</dt>
                        <dd className="text-lg font-medium text-gray-900">{dashboardData.refundedPayments}</dd>
                        <dt className="text-sm font-medium text-gray-500 truncate mt-1">Nel periodo selezionato</dt>
                        <dd className="text-sm text-purple-600">Completati</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Refunds Card */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Rimborsi Processati</dt>
                        <dd className="text-lg font-medium text-gray-900">{dashboardData.totalRefundRequests}</dd>
                        <dt className="text-sm font-medium text-gray-500 truncate mt-1">Importo Totale</dt>
                        <dd className="text-sm text-orange-600">{formatCurrency(dashboardData.totalRefundAmount)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">Nessun dato disponibile</p>
            </div>
          )}
        </div>
      );
    } else if (activeGeneraleTab === 'users') {
      return renderUsersContent();
    } else if (activeGeneraleTab === 'payments') {
      return renderPaymentsContent();
    } else if (activeGeneraleTab === 'refunds') {
      return renderRefundsContent();
    } else if (activeGeneraleTab === 'bookings') {
      return renderBookingsContent();
    } else if (activeGeneraleTab === 'discount-codes') {
      return renderDiscountCodesContent();
    }
    return null;
  };

  // Room Configuration handlers (removed - using hardcoded values)

  const loadTripDates = async (tripId: string) => {
    try {
      const response = await adminApi.getTripDates(tripId);
      setTripDates(response);
    } catch (error) {
      console.error('Error loading trip dates:', error);
    }
  };

  const loadTripPricing = async (tripDateId: string) => {
    try {
      const response = await adminApi.getTripPricing(tripDateId);
      setTripPricingList(response);
    } catch (error) {
      console.error('Error loading trip pricing:', error);
    }
  };

  const handleCreateTripDate = async () => {
    if (!selectedTrip || !newTripDate.startDate || !newTripDate.endDate) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    try {
      await adminApi.createTripDate(selectedTrip.id, newTripDate);
      setNewTripDate({ startDate: '', endDate: '' });
      loadTripDates(selectedTrip.id);
      alert('Data viaggio creata con successo!');
    } catch (error) {
      console.error('Error creating trip date:', error);
      alert('Errore nella creazione della data viaggio');
    }
  };

  const handleCreateTripPricing = async () => {
    if (!selectedTripDate || !newTripPricing.participationType || !newTripPricing.totalPrice) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    try {
      await adminApi.createTripPricing(selectedTripDate.id, newTripPricing);
      setNewTripPricing({
        participationType: '',
        occupancy: 1,
        totalPrice: 0,
        depositPerPerson: 0,
        secondTrancheAmount: 0
      });
      loadTripPricing(selectedTripDate.id);
      alert('Prezzo viaggio creato con successo!');
    } catch (error) {
      console.error('Error creating trip pricing:', error);
      alert('Errore nella creazione del prezzo viaggio');
    }
  };

  const handleDeleteTripPricing = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo prezzo viaggio?')) return;

    try {
      await adminApi.deleteTripPricing(id);
      if (selectedTripDate) {
        loadTripPricing(selectedTripDate.id);
      }
      alert('Prezzo viaggio eliminato con successo!');
    } catch (error) {
      console.error('Error deleting trip pricing:', error);
      alert('Errore nell\'eliminazione del prezzo viaggio');
    }
  };

  const handleCreateTrip = async () => {
    if (!newTrip.name || !newTrip.description || !newTrip.deposit || 
        !newTrip.priceSingle || !newTrip.priceDuo || !newTrip.priceTrio || !newTrip.pricePocker) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    try {
      // Crea il viaggio con tutti i dati
      await adminApi.createTrip(newTrip);

      // Reset form
      setNewTrip({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        maxParticipants: 20,
        isActive: true,
        deposit: 0,
        priceSingle: 0,
        priceDuo: 0,
        priceTrio: 0,
        pricePocker: 0
      });
      setShowCreateTripModal(false);
      loadTrips();
      alert('Viaggio creato con successo!');
    } catch (error) {
      console.error('Error creating trip:', error);
      alert('Errore nella creazione del viaggio');
    }
  };

  const renderTripConfigContent = () => (
    <div className="space-y-6">
      {/* Trip Configuration - Now using hardcoded values */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Configurazione Sistema Viaggi</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partecipanti Massimi per Viaggio
              </label>
              <input
                type="number"
                value={4}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">Valore fisso: 4 partecipanti</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Limite Età Bambini
              </label>
              <input
                type="number"
                value={16}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">Valore fisso: 16 anni</p>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> I valori di configurazione sono ora fissi nel sistema per semplificare la gestione.
            </p>
          </div>
        </div>
      </div>

      {/* Trip Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Seleziona Viaggio</h3>
        <select
          value={selectedTrip?.id || ''}
          onChange={(e) => {
            const trip = trips.find(t => t.id === e.target.value);
            setSelectedTrip(trip || null);
            if (trip) {
              loadTripDates(trip.id);
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Seleziona un viaggio</option>
          {trips.map(trip => (
            <option key={trip.id} value={trip.id}>{formatTripOption(trip)}</option>
          ))}
        </select>
      </div>

      {/* Trip Dates Management */}
      {selectedTrip && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Gestione Date Viaggio</h3>
          
          {/* Add New Date */}
          <div className="mb-6 p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">Aggiungi Nuova Data</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Inizio
                </label>
                <input
                  type="date"
                  value={newTripDate.startDate}
                  onChange={(e) => setNewTripDate({ ...newTripDate, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Fine
                </label>
                <input
                  type="date"
                  value={newTripDate.endDate}
                  onChange={(e) => setNewTripDate({ ...newTripDate, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <button
              onClick={handleCreateTripDate}
              className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Aggiungi Data
            </button>
          </div>

          {/* Existing Dates */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Date Esistenti</h4>
            {tripDates.length === 0 ? (
              <p className="text-gray-500">Nessuna data configurata per questo viaggio</p>
            ) : (
              <div className="space-y-3">
                {tripDates.map(date => (
                  <div key={date.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div>
                      <div className="font-medium">
                        {new Date(date.startDate).toLocaleDateString('it-IT')} - {new Date(date.endDate).toLocaleDateString('it-IT')}
                      </div>
                      <div className="text-sm text-gray-500">
                        {date.tripPricing?.length || 0} tipi di partecipazione configurati
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedTripDate(date);
                        loadTripPricing(date.id);
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Gestisci Prezzi
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trip Pricing Management */}
      {selectedTripDate && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Gestione Prezzi - {new Date(selectedTripDate.startDate).toLocaleDateString('it-IT')} - {new Date(selectedTripDate.endDate).toLocaleDateString('it-IT')}
          </h3>
          
          {/* Add New Pricing */}
          <div className="mb-6 p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">Aggiungi Nuovo Prezzo</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo Partecipazione
                </label>
                <select
                  value={newTripPricing.participationType}
                  onChange={(e) => {
                    const type = e.target.value;
                    const occupancy = type === 'solo' ? 1 : type === 'duo' ? 2 : type === 'family3' ? 3 : 4;
                    setNewTripPricing({ ...newTripPricing, participationType: type, occupancy });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleziona tipo</option>
                  <option value="solo">Solo Trip (1 persona)</option>
                  <option value="duo">Duo Trip (2 persone)</option>
                  <option value="family3">Family Trip 3 (3 persone)</option>
                  <option value="family4">Family Trip 4 (4 persone)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prezzo Totale (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newTripPricing.totalPrice}
                  onChange={(e) => setNewTripPricing({ ...newTripPricing, totalPrice: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Acconto per Persona (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newTripPricing.depositPerPerson}
                  onChange={(e) => setNewTripPricing({ ...newTripPricing, depositPerPerson: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seconda Tranche (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newTripPricing.secondTrancheAmount}
                  onChange={(e) => setNewTripPricing({ ...newTripPricing, secondTrancheAmount: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <button
              onClick={handleCreateTripPricing}
              className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Aggiungi Prezzo
            </button>
          </div>

          {/* Existing Pricing */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Prezzi Esistenti</h4>
            {tripPricingList.length === 0 ? (
              <p className="text-gray-500">Nessun prezzo configurato per questa data</p>
            ) : (
              <div className="space-y-3">
                {tripPricingList.map(pricing => (
                  <div key={pricing.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div>
                      <div className="font-medium">
                        {pricing.participationType === 'solo' && 'Solo Trip'}
                        {pricing.participationType === 'duo' && 'Duo Trip'}
                        {pricing.participationType === 'family3' && 'Family Trip 3'}
                        {pricing.participationType === 'family4' && 'Family Trip 4'}
                        {' '}- {pricing.occupancy} {pricing.occupancy === 1 ? 'persona' : 'persone'}
                      </div>
                      <div className="text-sm text-gray-500">
                        Totale: €{pricing.totalPrice.toFixed(2)} | Acconto: €{(pricing.depositPerPerson * pricing.occupancy).toFixed(2)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteTripPricing(pricing.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Elimina
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderDiscountCodesContent = () => {
    const handleCreateDiscountCode = async () => {
      if (!newDiscountCode.viaggioId || !newDiscountCode.nomeInfluencer || !newDiscountCode.cognomeInfluencer || newDiscountCode.percentualeSconto <= 0) {
        alert('Compila tutti i campi obbligatori');
        return;
      }

      try {
        await discountCodesApi.create(newDiscountCode);
        setNewDiscountCode({
          percentualeSconto: 0,
          viaggioId: '',
          nomeInfluencer: '',
          cognomeInfluencer: ''
        });
        loadDiscountCodes();
        alert('Codice sconto creato con successo!');
      } catch (error: any) {
        console.error('Error creating discount code:', error);
        alert(error.response?.data?.message || 'Errore nella creazione del codice sconto');
      }
    };

    const handleDeleteDiscountCode = async (id: string) => {
      if (!confirm('Sei sicuro di voler eliminare questo codice sconto?')) return;

      try {
        await discountCodesApi.delete(id);
        loadDiscountCodes();
        loadDiscountCodeStats();
        alert('Codice sconto eliminato con successo!');
      } catch (error: any) {
        console.error('Error deleting discount code:', error);
        alert(error.response?.data?.message || 'Errore nell\'eliminazione del codice sconto');
      }
    };

    const handleEditDiscountCode = (code: any) => {
      setSelectedDiscountCode(code);
      setEditDiscountCode({
        percentualeSconto: code.percentualeSconto,
        viaggioId: code.viaggioId,
        nomeInfluencer: code.nomeInfluencer,
        cognomeInfluencer: code.cognomeInfluencer
      });
      setShowEditDiscountCodeModal(true);
    };

    const handleUpdateDiscountCode = async () => {
      if (!selectedDiscountCode) return;
      if (!editDiscountCode.viaggioId || !editDiscountCode.nomeInfluencer || !editDiscountCode.cognomeInfluencer || editDiscountCode.percentualeSconto <= 0) {
        alert('Compila tutti i campi obbligatori');
        return;
      }

      try {
        await discountCodesApi.update(selectedDiscountCode.id, editDiscountCode);
        setShowEditDiscountCodeModal(false);
        setSelectedDiscountCode(null);
        loadDiscountCodes();
        loadDiscountCodeStats();
        alert('Codice sconto aggiornato con successo!');
      } catch (error: any) {
        console.error('Error updating discount code:', error);
        alert(error.response?.data?.message || 'Errore nell\'aggiornamento del codice sconto');
      }
    };

    return (
      <div className="space-y-6">
        {/* Statistics Overview */}
        {discountCodeStats && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Statistiche Codici Sconto</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Pagamenti con Codice Sconto</div>
                <div className="text-2xl font-bold text-blue-600">{discountCodeStats.totalPaymentsWithDiscount || 0}</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Totale Sconti Applicati</div>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(discountCodeStats.totalDiscountAmount || 0)}</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Codici Unici Utilizzati</div>
                <div className="text-2xl font-bold text-purple-600">{discountCodeStats.uniqueCodesUsed || 0}</div>
              </div>
            </div>
          </div>
        )}

        {/* Create New Discount Code */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Crea Nuovo Codice Sconto</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Viaggio *</label>
              <select
                value={newDiscountCode.viaggioId}
                onChange={(e) => setNewDiscountCode({ ...newDiscountCode, viaggioId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleziona viaggio</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>{formatTripOption(trip)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Percentuale Sconto (%) *</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={newDiscountCode.percentualeSconto}
                onChange={(e) => setNewDiscountCode({ ...newDiscountCode, percentualeSconto: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome Influencer *</label>
              <input
                type="text"
                value={newDiscountCode.nomeInfluencer}
                onChange={(e) => setNewDiscountCode({ ...newDiscountCode, nomeInfluencer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cognome Influencer *</label>
              <input
                type="text"
                value={newDiscountCode.cognomeInfluencer}
                onChange={(e) => setNewDiscountCode({ ...newDiscountCode, cognomeInfluencer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <button
            onClick={handleCreateDiscountCode}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Crea Codice Sconto
          </button>
        </div>

        {/* Discount Codes List */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Lista Codici Sconto</h3>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : discountCodes.length === 0 ? (
            <p className="text-gray-500">Nessun codice sconto disponibile</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Codice</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Viaggio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sconto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilizzi</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Creazione</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {discountCodes.map((code: any) => (
                    <tr key={code.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono font-semibold text-blue-600">{code.codice}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{code.trip?.name || 'N/A'}</div>
                          {code.trip?.startDate && (
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(code.trip.startDate).toLocaleDateString('it-IT')}
                              {code.trip.endDate && code.trip.endDate !== code.trip.startDate && 
                                ` - ${new Date(code.trip.endDate).toLocaleDateString('it-IT')}`
                              }
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {code.nomeInfluencer} {code.cognomeInfluencer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {code.percentualeSconto}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {code._count?.payments || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(code.createdAt).toLocaleDateString('it-IT')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          code._count?.payments > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {code._count?.payments > 0 ? 'In uso' : 'Disponibile'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditDiscountCode(code)}
                            className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md"
                          >
                            Modifica
                          </button>
                          <button
                            onClick={() => handleDeleteDiscountCode(code.id)}
                            className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md"
                            disabled={code._count?.payments > 0}
                          >
                            Elimina
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Discount Codes Usage Statistics */}
        {discountCodeStats && discountCodeStats.codesBreakdown && discountCodeStats.codesBreakdown.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Statistiche Dettagliate per Codice</h3>
            <div className="space-y-4">
              {discountCodeStats.codesBreakdown.map((codeBreakdown: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-mono font-semibold text-blue-600">{codeBreakdown.codice}</span>
                      <span className="ml-2 text-sm text-gray-600">
                        - {codeBreakdown.nomeInfluencer} {codeBreakdown.cognomeInfluencer}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {codeBreakdown.percentualeSconto}% di sconto
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    <div>
                      <div className="text-xs text-gray-500">Utilizzi</div>
                      <div className="text-lg font-semibold">{codeBreakdown.usageCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Importo Originale Totale</div>
                      <div className="text-lg font-semibold">{formatCurrency(codeBreakdown.totalOriginalAmount)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Importo Finale Totale</div>
                      <div className="text-lg font-semibold text-green-600">{formatCurrency(codeBreakdown.totalFinalAmount)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Totale Sconto</div>
                      <div className="text-lg font-semibold text-purple-600">{formatCurrency(codeBreakdown.totalDiscount)}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Viaggio: {codeBreakdown.trip?.name || 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Verification Results */}
        {discountCodeStats && discountCodeStats.verificationResults && discountCodeStats.verificationResults.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Verifica Correttezza Sconti Applicati</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Codice</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Importo Originale</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sconto %</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Importo Finale Atteso</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Importo Finale Reale</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Differenza</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {discountCodeStats.verificationResults.map((verification: any, index: number) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-blue-600">
                        {verification.codice}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(verification.importoOriginale)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {verification.percentualeScontoApplicata}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(verification.expectedFinal)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(verification.importoFinale)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(verification.difference)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {verification.isCorrect ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            ✓ Corretto
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            ✗ Errore
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Influencer Earnings Base Data */}
        {discountCodeStats && discountCodeStats.codesBreakdown && discountCodeStats.codesBreakdown.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Base Dati per Calcolo Guadagni Influencer</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Codice</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Viaggio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numero Utilizzi</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Importo Totale Venduto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Totale Sconti Applicati</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Importo Finale Incassato</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {discountCodeStats.codesBreakdown.map((codeBreakdown: any, index: number) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {codeBreakdown.nomeInfluencer} {codeBreakdown.cognomeInfluencer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-blue-600">
                        {codeBreakdown.codice}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {codeBreakdown.trip?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {codeBreakdown.usageCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(codeBreakdown.totalOriginalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 font-semibold">
                        {formatCurrency(codeBreakdown.totalDiscount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                        {formatCurrency(codeBreakdown.totalFinalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Discount Code Modal */}
        {showEditDiscountCodeModal && selectedDiscountCode && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Modifica Codice Sconto</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Codice</label>
                    <input
                      type="text"
                      value={selectedDiscountCode.codice}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Viaggio *</label>
                    <select
                      value={editDiscountCode.viaggioId}
                      onChange={(e) => setEditDiscountCode({ ...editDiscountCode, viaggioId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seleziona viaggio</option>
                      {trips.map(trip => (
                        <option key={trip.id} value={trip.id}>{formatTripOption(trip)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentuale Sconto (%) *</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={editDiscountCode.percentualeSconto}
                      onChange={(e) => setEditDiscountCode({ ...editDiscountCode, percentualeSconto: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome Influencer *</label>
                    <input
                      type="text"
                      value={editDiscountCode.nomeInfluencer}
                      onChange={(e) => setEditDiscountCode({ ...editDiscountCode, nomeInfluencer: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cognome Influencer *</label>
                    <input
                      type="text"
                      value={editDiscountCode.cognomeInfluencer}
                      onChange={(e) => setEditDiscountCode({ ...editDiscountCode, cognomeInfluencer: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditDiscountCodeModal(false);
                      setSelectedDiscountCode(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleUpdateDiscountCode}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Salva Modifiche
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderViaggiContent = () => {
    if (activeViaggiTab === 'trips') {
      return renderTripsContent();
    } else if (activeViaggiTab === 'destinations') {
      return (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Gestione Destinazioni</h3>
          <p className="text-gray-600">Funzionalità in sviluppo...</p>
        </div>
      );
    } else if (activeViaggiTab === 'activities') {
      return (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Gestione Attività</h3>
          <p className="text-gray-600">Funzionalità in sviluppo...</p>
        </div>
      );
    }
    return null;
  };

  const renderProdottiContent = () => {
    if (activeProdottiTab === 'products') {
      return renderProductsContent();
    } else if (activeProdottiTab === 'categories') {
      return renderCategoriesContent();
    } else if (activeProdottiTab === 'product-orders') {
      return renderProductOrdersContent();
    } else if (activeProdottiTab === 'shipping-costs') {
      return renderShippingCostsContent();
    }
    return null;
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Amministratore</h1>
        <p className="mt-2 text-gray-600">
          Gestisci utenti, viaggi, pagamenti e richieste di rimborso
        </p>
      </div>

      {/* Generale Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Generale</h2>
        
        {/* Generale Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {generaleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveGeneraleTab(tab.id);
                  setCurrentPage(1);
                  setSearchTerm('');
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeGeneraleTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Generale Content */}
        {renderGeneraleContent()}
      </div>

      {/* Viaggi Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Viaggi</h2>
        
        {/* Viaggi Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {viaggiTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveViaggiTab(tab.id);
                  setCurrentPage(1);
                  setSearchTerm('');
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeViaggiTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Viaggi Content */}
        {renderViaggiContent()}
      </div>

      {/* Prodotti Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Prodotti</h2>
        
        {/* Prodotti Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {prodottiTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveProdottiTab(tab.id);
                  setCurrentPage(1);
                  setSearchTerm('');
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeProdottiTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Prodotti Content */}
        {renderProdottiContent()}
      </div>

      {/* Calcolatore Costi Viaggio Section */}
      <div className="mb-12">
        <TripCostCalculator />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Precedente
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Successivo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Pagina <span className="font-medium">{currentPage}</span> di{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Precedente
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Successivo
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Refund Management Modal */}
      {selectedRefund && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Gestisci Richiesta di Rimborso
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Utente:</strong> {selectedRefund.user?.email}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Viaggio:</strong> {selectedRefund.payment?.trip?.name}
                  {selectedRefund.payment?.trip?.startDate && (
                    <span className="ml-2 text-xs text-gray-500">
                      ({new Date(selectedRefund.payment.trip.startDate).toLocaleDateString('it-IT')}
                      {selectedRefund.payment.trip.endDate && selectedRefund.payment.trip.endDate !== selectedRefund.payment.trip.startDate && 
                        ` - ${new Date(selectedRefund.payment.trip.endDate).toLocaleDateString('it-IT')}`
                      })
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Importo:</strong> {formatCurrency(selectedRefund.payment?.amount || 0)}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Motivo:</strong> {selectedRefund.reason}
                </p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stato Attuale: <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedRefund.status)}`}>
                    {selectedRefund.status === 'pending' ? 'In Attesa' :
                     selectedRefund.status === 'approved' ? 'Approvato' :
                     selectedRefund.status === 'rejected' ? 'Rifiutato' :
                     selectedRefund.status === 'processed' ? 'Processato' : selectedRefund.status}
                  </span>
                </label>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cambia Stato
                </label>
                <select
                  value={refundStatus}
                  onChange={(e) => setRefundStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">In Attesa</option>
                  <option value="approved">Approvato</option>
                  <option value="rejected">Rifiutato</option>
                  <option value="processed">Processato</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note Amministratore
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Inserisci note opzionali..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedRefund(null);
                    setRefundStatus('');
                    setAdminNotes('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Annulla
                </button>
                <button
                  onClick={handleRefundStatusUpdate}
                  disabled={!refundStatus || refundStatus === selectedRefund.status}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {refundStatus === selectedRefund.status ? 'Nessuna Modifica' : 'Aggiorna'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trip Pricing Modal */}
      {selectedTrip && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Modifica - {selectedTrip.name}
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Viaggio
                </label>
                <input
                  type="text"
                  value={selectedTrip.name}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrizione
                </label>
                <textarea
                  value={selectedTrip.description}
                  disabled
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Viaggio
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="date"
                    value={selectedTrip.startDate ? new Date(selectedTrip.startDate).toISOString().split('T')[0] : ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                  <input
                    type="date"
                    value={selectedTrip.endDate ? new Date(selectedTrip.endDate).toISOString().split('T')[0] : ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Acconto per Persona (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={tripPricing.deposit}
                  onChange={(e) => setTripPricing(prev => ({ ...prev, deposit: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numero Massimo Partecipanti
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={tripPricing.maxParticipants}
                  onChange={(e) => setTripPricing(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 20 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prezzi per Partecipazione (€) — per persona
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Solo (1 persona) — prezzo per persona</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tripPricing.priceSingle}
                      onChange={(e) => setTripPricing(prev => ({ ...prev, priceSingle: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Duo (2 persone) — prezzo per persona</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tripPricing.priceDuo}
                      onChange={(e) => setTripPricing(prev => ({ ...prev, priceDuo: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Family 3 (3 persone) — prezzo per persona</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tripPricing.priceTrio}
                      onChange={(e) => setTripPricing(prev => ({ ...prev, priceTrio: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Family 4 (4 persone) — prezzo per persona</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tripPricing.pricePocker}
                      onChange={(e) => setTripPricing(prev => ({ ...prev, pricePocker: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stato del Viaggio
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="isActive"
                      checked={tripPricing.isActive === true}
                      onChange={() => setTripPricing(prev => ({ ...prev, isActive: true }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Attivo</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="isActive"
                      checked={tripPricing.isActive === false}
                      onChange={() => setTripPricing(prev => ({ ...prev, isActive: false }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Non Attivo</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  I viaggi non attivi non saranno visibili ai clienti
                </p>
              </div>


              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedTrip(null);
                    setTripPricing({
                      deposit: 0,
                      priceSingle: 0,
                      priceDuo: 0,
                      priceTrio: 0,
                      pricePocker: 0,
                      maxParticipants: 20,
                      isActive: true
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Annulla
                </button>
                <button
                  onClick={handleTripPricingUpdate}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Aggiorna
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Trip Modal */}
      {showCreateTripModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Crea Nuovo Viaggio
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informazioni Viaggio */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Informazioni Viaggio</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Viaggio *
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Il nome del viaggio deve essere uguale all'ID nella URL (es. 'Summer%20Trip' corrisponde a 'Summer Trip'). Obbligatorio per tutti i viaggi dello stesso tipo.
                    </p>
                    <input
                      type="text"
                      value={newTrip.name}
                      onChange={(e) => setNewTrip({ ...newTrip, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Es. Summer Trip 2024"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descrizione *
                    </label>
                    <textarea
                      value={newTrip.description}
                      onChange={(e) => setNewTrip({ ...newTrip, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Descrizione del viaggio..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data Inizio *
                      </label>
                      <input
                        type="date"
                        value={newTrip.startDate}
                        onChange={(e) => setNewTrip({ ...newTrip, startDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data Fine *
                      </label>
                      <input
                        type="date"
                        value={newTrip.endDate}
                        onChange={(e) => setNewTrip({ ...newTrip, endDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Partecipanti Massimi
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newTrip.maxParticipants}
                      onChange={(e) => setNewTrip({ ...newTrip, maxParticipants: parseInt(e.target.value) || 20 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newTrip.isActive}
                        onChange={(e) => setNewTrip({ ...newTrip, isActive: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Viaggio Attivo</span>
                    </label>
                  </div>
                </div>

                {/* Configurazione Prezzi */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Configurazione Prezzi</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Deposito per Persona *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={newTrip.deposit}
                      onChange={(e) => setNewTrip({ ...newTrip, deposit: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prezzo Solo (1 persona) — per persona *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newTrip.priceSingle}
                        onChange={(e) => setNewTrip({ ...newTrip, priceSingle: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prezzo Duo (2 persone) — per persona *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newTrip.priceDuo}
                        onChange={(e) => setNewTrip({ ...newTrip, priceDuo: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prezzo Family 3 (3 persone) — per persona *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newTrip.priceTrio}
                        onChange={(e) => setNewTrip({ ...newTrip, priceTrio: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prezzo Family 4 (4 persone) — per persona *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newTrip.pricePocker}
                        onChange={(e) => setNewTrip({ ...newTrip, pricePocker: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateTripModal(false);
                    setNewTrip({
                      name: '',
                      description: '',
                      startDate: '',
                      endDate: '',
                      maxParticipants: 20,
                      isActive: true,
                      deposit: 0,
                      priceSingle: 0,
                      priceDuo: 0,
                      priceTrio: 0,
                      pricePocker: 0
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Annulla
                </button>
                <button
                  onClick={handleCreateTrip}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  Crea Viaggio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {selectedPaymentParticipants && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Partecipanti - {selectedPaymentParticipants.trip?.name}
                {selectedPaymentParticipants.trip?.startDate && (
                  <div className="text-sm text-gray-500 mt-1">
                    {new Date(selectedPaymentParticipants.trip.startDate).toLocaleDateString('it-IT')}
                    {selectedPaymentParticipants.trip.endDate && selectedPaymentParticipants.trip.endDate !== selectedPaymentParticipants.trip.startDate && 
                      ` - ${new Date(selectedPaymentParticipants.trip.endDate).toLocaleDateString('it-IT')}`
                    }
                  </div>
                )}
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Utente:</strong> {selectedPaymentParticipants.user?.email}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Importo:</strong> {formatCurrency(selectedPaymentParticipants.amount)}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Tranche:</strong> {selectedPaymentParticipants.tranche === 1 ? 'Acconto' : 'Saldo'}
                </p>
              </div>

              {selectedPaymentParticipants.participantCount > 0 ? (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Lista Partecipanti ({selectedPaymentParticipants.participantCount})
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedPaymentParticipants.participants.map((participant, index) => (
                      <div key={participant.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {index + 1}. {participant.firstName} {participant.lastName}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500">Nessun partecipante registrato</p>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setSelectedPaymentParticipants(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {selectedProduct.id ? 'Modifica Prodotto' : 'Aggiungi Prodotto'}
              </h3>
              
              <form onSubmit={handleProductSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={selectedProduct.name || ''}
                    onChange={(e) => setSelectedProduct(prev => prev ? { ...prev, name: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                

                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={selectedProduct.price || ''}
                    onChange={(e) => setSelectedProduct(prev => prev ? { ...prev, price: parseFloat(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                  <input
                    type="number"
                    value={selectedProduct.stock || ''}
                    onChange={(e) => setSelectedProduct(prev => prev ? { ...prev, stock: parseInt(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <select
                    value={selectedProduct.category?.id || ''}
                    onChange={(e) => setSelectedProduct(prev => prev ? { ...prev, categoryId: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleziona categoria</option>
                    {Array.isArray(categories) && categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                

                
                {selectedProduct.id && (
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedProduct.isActive !== false}
                        onChange={(e) => setSelectedProduct(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Prodotto attivo</span>
                    </label>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setSelectedProduct(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    {selectedProduct.id ? 'Aggiorna' : 'Crea'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Product Order Modal */}
      {selectedProductOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Dettagli Ordine #{selectedProductOrder.id.slice(-8)}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">
                    <strong>Cliente:</strong> {selectedProductOrder.user?.email || selectedProductOrder.user?.username}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Data:</strong> {formatDate(selectedProductOrder.createdAt)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Stato:</strong> {getStatusLabel(selectedProductOrder.status)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Totale:</strong> {formatCurrency(selectedProductOrder.totalAmount)}
                  </p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Prodotti:</h4>
                  <div className="space-y-2">
                    {selectedProductOrder.items.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span>{item.product.name} x{item.quantity}</span>
                        <span>{formatCurrency(item.priceAtTime * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {selectedProductOrder.shippingAddress && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Indirizzo di spedizione:</h4>
                    <p className="text-sm text-gray-600">
                      {selectedProductOrder.shippingAddress}<br />
                      {selectedProductOrder.shippingCity} {selectedProductOrder.shippingPostalCode}<br />
                      {selectedProductOrder.shippingCountry}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aggiorna Stato</label>
                  <select
                    value={selectedProductOrder.status}
                    onChange={(e) => handleOrderStatusUpdate(selectedProductOrder.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">In attesa</option>
                    <option value="paid">Pagato</option>
                    <option value="shipped">Spedito</option>
                    <option value="delivered">Consegnato</option>
                    <option value="cancelled">Annullato</option>
                  </select>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={() => setSelectedProductOrder(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories Tab - REMOVED */}
      {false && (
        <div>
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Gestione Categorie</h2>
            <button
              onClick={() => setSelectedCategory({ id: '', name: '', slug: '', isActive: true, createdAt: '', updatedAt: '' })}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Aggiungi Categoria
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Caricamento categorie...</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {Array.isArray(categories) && categories.map((category) => (
                  <li key={category.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">{category.name}</p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              category.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {category.isActive ? 'Attiva' : 'Inattiva'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">Slug: {category.slug}</p>
                          <p className="text-sm text-gray-500">Creata: {formatDate(category.createdAt)}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedCategory(category)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-red-600 hover:text-red-900 text-sm font-medium"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Shipping Cost Modal */}
      {selectedShippingCost && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {selectedShippingCost.id ? 'Modifica Costo di Consegna' : 'Aggiungi Costo di Consegna'}
              </h3>
              
              <form onSubmit={handleShippingCostSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug Nome</label>
                  <input
                    type="text"
                    value={selectedShippingCost.nameSlug || ''}
                    onChange={(e) => setSelectedShippingCost(prev => prev ? { ...prev, nameSlug: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="es. free_shipping, standard_shipping"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Costo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={selectedShippingCost.cost || ''}
                    onChange={(e) => setSelectedShippingCost(prev => prev ? { ...prev, cost: parseFloat(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Importo Minimo Ordine (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={selectedShippingCost.minOrderAmount || ''}
                    onChange={(e) => setSelectedShippingCost(prev => prev ? { ...prev, minOrderAmount: e.target.value ? parseFloat(e.target.value) : null } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Lasciare vuoto per nessun minimo"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paesi (codici separati da virgola)</label>
                  <input
                    type="text"
                    value={selectedShippingCost.countries?.join(', ') || ''}
                    onChange={(e) => setSelectedShippingCost(prev => prev ? { ...prev, countries: e.target.value.split(',').map(c => c.trim().toUpperCase()) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="es. NL, BE, DE"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordine di Visualizzazione</label>
                  <input
                    type="number"
                    value={selectedShippingCost.sortOrder || ''}
                    onChange={(e) => setSelectedShippingCost(prev => prev ? { ...prev, sortOrder: parseInt(e.target.value) } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedShippingCost.isActive}
                      onChange={(e) => setSelectedShippingCost(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Attivo</span>
                  </label>
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedShippingCost.isDefault}
                      onChange={(e) => setSelectedShippingCost(prev => prev ? { ...prev, isDefault: e.target.checked } : null)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Opzione Predefinita</span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setSelectedShippingCost(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    {selectedShippingCost.id ? 'Aggiorna' : 'Crea'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Costs Tab - REMOVED */}
      {false && (
        <div>
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Gestione Costi di Consegna</h2>
            <button
              onClick={() => setSelectedShippingCost({ 
                id: '', 
                nameSlug: '', 
                cost: 0, 
                minOrderAmount: null, 
                isActive: true, 
                isDefault: false, 
                sortOrder: 0, 
                countries: ['NL'],
                createdAt: '', 
                updatedAt: '' 
              })}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Aggiungi Costo di Consegna
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Caricamento costi di consegna...</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {Array.isArray(shippingCosts) && shippingCosts.map((shippingCost) => (
                  <li key={shippingCost.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">{shippingCost.nameSlug}</p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              shippingCost.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {shippingCost.isActive ? 'Attivo' : 'Inattivo'}
                            </span>
                            {shippingCost.isDefault && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Predefinito
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            Costo: €{shippingCost.cost.toFixed(2)}
                            {shippingCost.minOrderAmount && ` | Min. ordine: €${shippingCost.minOrderAmount.toFixed(2)}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            Paesi: {shippingCost.countries.join(', ')}
                          </p>
                          <p className="text-sm text-gray-500">Ordine: {shippingCost.sortOrder}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedShippingCost(shippingCost)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => handleDeleteShippingCost(shippingCost.id)}
                          className="text-red-600 hover:text-red-900 text-sm font-medium"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Category Modal */}
      {selectedCategory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {selectedCategory.id ? 'Modifica Categoria' : 'Aggiungi Categoria'}
              </h3>
              
              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={selectedCategory.name || ''}
                    onChange={(e) => setSelectedCategory(prev => prev ? { ...prev, name: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input
                    type="text"
                    value={selectedCategory.slug || ''}
                    onChange={(e) => setSelectedCategory(prev => prev ? { ...prev, slug: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                {selectedCategory.id && (
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedCategory.isActive}
                        onChange={(e) => setSelectedCategory(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Categoria attiva</span>
                    </label>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setSelectedCategory(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    {selectedCategory.id ? 'Aggiorna' : 'Crea'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Sales Report Modal */}
      {showSalesReport && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Report Vendite ({formatDate(salesReportDateRange.startDate)} - {formatDate(salesReportDateRange.endDate)})
                </h3>
                <button
                  onClick={() => setShowSalesReport(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Filter Controls */}
              <div className="bg-gray-50 p-4 rounded-md mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID Prodotto</label>
                  <input
                    type="text"
                    value={(salesReportFilters?.productId) || ''}
                    onChange={(e) => setSalesReportFilters((prev: any) => ({ ...prev, productId: e.target.value }))}
                    placeholder="Optional"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <input
                    type="text"
                    value={(salesReportFilters?.category) || ''}
                    onChange={(e) => setSalesReportFilters((prev: any) => ({ ...prev, category: e.target.value }))}
                    placeholder="cheese, wine, ..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email/Username Cliente</label>
                  <input
                    type="text"
                    value={(salesReportFilters?.customerEmail) || ''}
                    onChange={(e) => setSalesReportFilters((prev: any) => ({ ...prev, customerEmail: e.target.value }))}
                    placeholder="Optional"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="md:col-span-4 flex justify-end">
                  <button
                    onClick={generateSalesReport}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Applica filtri
                  </button>
                </div>
              </div>

              {salesReportLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Generazione report...</p>
                </div>
              ) : salesReport ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-800">Fatturato Totale</h4>
                      <p className="text-2xl font-bold text-blue-900">{formatCurrency(salesReport.totalRevenue)}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-green-800">Ordini Totali</h4>
                      <p className="text-2xl font-bold text-green-900">{salesReport.totalOrders}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-purple-800">Clienti Unici</h4>
                      <p className="text-2xl font-bold text-purple-900">{Object.keys(salesReport.customerSales).length}</p>
                    </div>
                  </div>
                  
                  {/* Product Sales */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Vendite per Prodotto</h4>
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <ul className="divide-y divide-gray-200">
                        {Object.entries(salesReport.productSales).map(([productName, data]: [string, any]) => (
                          <li key={productName} className="px-6 py-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{productName}</p>
                                <p className="text-sm text-gray-500">
                                  Quantità venduta: {data.quantity} | Ordini: {data.orders}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-gray-900">{formatCurrency(data.revenue)}</p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Customer Sales */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Vendite per Cliente</h4>
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <ul className="divide-y divide-gray-200">
                        {Object.entries(salesReport.customerSales).map(([customerEmail, data]: [string, any]) => (
                          <li key={customerEmail} className="px-6 py-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{customerEmail}</p>
                                <p className="text-sm text-gray-500">
                                  Ordini: {data.orders} | Ultimo ordine: {formatDate(data.lastOrder)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-gray-900">{formatCurrency(data.revenue)}</p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">Nessun dato disponibile per il periodo selezionato</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage; 